/**
 * @group Classes
 * 
 * @summary
 * <blockquote>HTTP service for onboarding API endpoints.</blockquote>
 * 
 * This service provides a typed interface for making HTTP requests to onboarding endpoints,
 * encapsulating API endpoint details and improving maintainability.
 * 
 * @example
 * ```typescript
 * import { OnboardingHttpService } from '@/services/http/OnboardingHttpService';
 * 
 * const service = new OnboardingHttpService();
 * const result = await service.startOnboarding({
 *   userId: 'user-123',
 *   tenantUUID: '550e8400-e29b-41d4-a716-446655440000',
 * });
 * 
 * if (result.isErr) {
 *   const error = result.getFail();
 *   console.error('Failed to start onboarding:', error.errorMessage);
 * } else {
 *   const response = result.getSuccess();
 *   console.log('Onboarding started:', response.data);
 * }
 * ```
 */
import { FetchApiClient } from '@shared/httpService';
import type { AsyncResult, AppError } from '@shared/utils';
import type { ApiResponse } from '@shared/httpService';

/**
 * Request payload for starting onboarding.
 */
type StartOnboardingRequest = {
  userId: string;
  tenantUUID?: string;
};

/**
 * Response payload from onboarding start API.
 */
type StartOnboardingResponse = {
  message: string;
  onboardingBlocked: boolean;
  onboardingBlockReason:
    | 'invalid_tenant_uuid'
    | 'none'
    | 'tenant_not_found'
    | 'tenant_uuid_tampered';
  pathwayType: 'public_default' | 'voluntary_group';
  tenantLinked: boolean;
};

export class OnboardingHttpService {
  private fetchApi: FetchApiClient;

  constructor() {
    this.fetchApi = new FetchApiClient();
  }

  /**
   * Starts onboarding assignment after signup has completed account creation.
   * 
   * @param data - The onboarding start request data
   * @returns AsyncResult containing the API response with onboarding data
   */
  async startOnboarding(
    data: StartOnboardingRequest
  ): AsyncResult<ApiResponse<StartOnboardingResponse>, AppError> {
    return await this.fetchApi.post<StartOnboardingResponse>(
      '/api/auth/onboarding/start',
      data
    );
  }
}
